import React, { useRef, useEffect, useCallback } from 'react';
import type { Theme } from '../../context/ThemeContext';

const GRID_SIZE = 24;
const FISHEYE_RADIUS = 200;
const FISHEYE_STRENGTH = 0.12;
const RESOLUTION_SCALE = 0.5; // draw at half res for performance

function getGridColor(theme: Theme): string {
  if (theme === 'dark') return 'rgba(56, 189, 248, 0.12)';
  if (theme === 'light') return 'rgba(75, 85, 99, 0.1)';
  return 'rgba(0, 0, 0, 0.08)';
}

interface GridFisheyeCanvasProps {
  mouseX: number;
  mouseY: number;
  theme: Theme;
}

export const GridFisheyeCanvas: React.FC<GridFisheyeCanvasProps> = ({ mouseX, mouseY, theme }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const offscreenRef = useRef<HTMLCanvasElement | null>(null);
  const drawGrid = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      const color = getGridColor(theme);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      for (let x = 0; x <= w + GRID_SIZE; x += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y <= h + GRID_SIZE; y += GRID_SIZE) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
    },
    [theme]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const w = Math.floor(canvas.clientWidth * RESOLUTION_SCALE);
    const h = Math.floor(canvas.clientHeight * RESOLUTION_SCALE);
    if (w <= 0 || h <= 0) return;

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (!offscreenRef.current || offscreenRef.current.width !== w || offscreenRef.current.height !== h) {
      offscreenRef.current = document.createElement('canvas');
      offscreenRef.current.width = w;
      offscreenRef.current.height = h;
    }
    const off = offscreenRef.current;
    const offCtx = off.getContext('2d');
    if (!offCtx) return;

    const cx = (mouseX / window.innerWidth) * w;
    const cy = (mouseY / window.innerHeight) * h;
    const radius = (FISHEYE_RADIUS / window.innerWidth) * w;

    offCtx.clearRect(0, 0, w, h);
    drawGrid(offCtx, w, h);

    const srcData = offCtx.getImageData(0, 0, w, h);
    const dstData = ctx.createImageData(w, h);
    const src = srcData.data;
    const dst = dstData.data;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const dx = x - cx;
        const dy = y - cy;
        const d = Math.sqrt(dx * dx + dy * dy) || 0.001;
        const t = Math.min(d / radius, 1);
        const pull = 1 - FISHEYE_STRENGTH * Math.exp(-t * t * 2);
        const dIn = d * pull;
        const sx = cx + (dx / d) * dIn;
        const sy = cy + (dy / d) * dIn;
        const sx0 = Math.max(0, Math.min(w - 1, Math.floor(sx)));
        const sy0 = Math.max(0, Math.min(h - 1, Math.floor(sy)));
        const dstI = (y * w + x) * 4;
        const srcJ = (sy0 * w + sx0) * 4;
        const alpha = 1 - t * t;
        dst[dstI] = src[srcJ];
        dst[dstI + 1] = src[srcJ + 1];
        dst[dstI + 2] = src[srcJ + 2];
        dst[dstI + 3] = Math.round((src[srcJ + 3] || 0) * alpha);
      }
    }

    ctx.putImageData(dstData, 0, 0);
  }, [mouseX, mouseY, theme, drawGrid]);

  return (
    <canvas
      ref={canvasRef}
      className="grid-fisheye-canvas"
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};
